import apiMultipart from "../config/api-multipart";
import {RsDocumentFrontResult} from "./dto/RsDocumentFrontResult";
import {AxiosResponse} from "axios";
import {Response} from "./dto/Response"
import {RsImageResult} from "./dto/RsImageResult";
import {RsInitiateVerification} from "./dto/RsInitiateVerification";
import initApi from "../config/initiate-verify";
import {RsVerification} from "./dto/RsVerification";
import {RqInputInformation} from "./dto/RqInputInformation";
import apiJson from "../config/api-json";
import {convertStringIntoVerificationStepType} from "../domain/verification/VerificationStepType";
import {DocumentType} from "../domain/verification/DocumentType";
import {RsVerificationResult} from "./dto/RsVerificationResult";
import {ResultStatusType} from "../domain/verification/ResultStatusType";
import {MismatchedStepError} from "../domain/exception/MismatchedStepError";
import {RsCurrentStep} from "./dto/RsCurrentStep";
import {getUserInfo} from "./APIUtils";
import {RqChooseDocument} from "./dto/RqChooseDocument";
import {RsDocumentType} from "./dto/RsDocumentType";
import {RqManuallySkipStep} from "./dto/RqManuallySkipStep";

export class VerifyAPI {

    baseUrl = "/verify"

    async initiateVerification(): Promise<RsInitiateVerification> {
        const response: AxiosResponse<Response<RsInitiateVerification>> = await initApi.post(`${this.baseUrl}/initiate`, { callbackUrl: "" })
        if (response.status !== 200) {
            throw new Error(`VerifyAPI initiateVerification: ${response.statusText}`)
        }
        return response.data.data
    }

    async giveConsent(verificationId: string): Promise<void> {
        await this.sendJsonRequest(`consent/${verificationId}`, "PATCH", verificationId, undefined)
    }

    async selectDocumentTemplate(verificationId: string, chosenDocumentId: string): Promise<void> {
        await this.sendJsonRequest<RqChooseDocument>(
            `choose-document/${verificationId}`,
            "PUT",
            verificationId,
            new RqChooseDocument(chosenDocumentId))
    }

    async getDocumentType(verificationId: string): Promise<RsDocumentType> {
        let token = localStorage.getItem("next-step")
        const config = await getUserInfo(verificationId, token)
        const response: AxiosResponse<Response<RsDocumentType>> = await apiJson.get(`${this.baseUrl}/type/${verificationId}`, config)
        if (response.status !== 200) {
            throw new Error(`VerifyAPI getDocumentType: ${response.statusText}`)
        }
        return response.data.data
    }

    async sendDocumentFrontImage(blob: Blob, verificationId: string, documentType: DocumentType | undefined): Promise<RsDocumentFrontResult> {
        const formData = new FormData();
        formData.append("document", blob, "document-front.png");
        formData.append("verification-id", verificationId)
        if (documentType) {
            formData.append("document-type", documentType)
        }
        return this.sendMultiPartRequest<RsDocumentFrontResult>(formData, "analyse-document-front", verificationId)
    }

    async getVerification(verificationId: string): Promise<RsVerification> {
        let token = localStorage.getItem("next-step")
        localStorage.removeItem("next-step")
        const config = await getUserInfo(verificationId, token)
        const response: AxiosResponse<Response<RsVerification>> = await apiJson.get(`${this.baseUrl}/retrieve-info/${verificationId}`, config)
        if (response.status !== 200) {
            throw new Error(`VerifyAPI getVerification: ${response.statusText}`)
        }
        return response.data.data
    }

    async sendDocumentBackImage(blob: Blob, verificationId: string, documentType?: DocumentType): Promise<RsImageResult> {
        const formData = new FormData();
        formData.append("document", blob, "document-back.png");
        formData.append("verification-id", verificationId)
        if (documentType) {
            formData.append("document-type", documentType)
        }
        return this.sendMultiPartRequest<RsImageResult>(formData, "analyse-document-back", verificationId)
    }

    async sendSelfieImage(blob: Blob, verificationId: string): Promise<RsImageResult> {
        const formData = new FormData();
        formData.append("selfie", blob, "selfie.png");
        formData.append("verification-id", verificationId)
        return await this.sendMultiPartRequest<RsImageResult>(formData, "analyse-biometrics", verificationId)
    }

    async sendInputInformation(inputInformation: RqInputInformation, verificationId: string) {
        await this.sendJsonRequest<RqInputInformation>(`input-info/${verificationId}`, "POST", verificationId, inputInformation)
    }

    async getResult(verificationId: string): Promise<RsVerificationResult> {
        let token = localStorage.getItem("next-step")
        const config = await getUserInfo(verificationId, token)
        const response: AxiosResponse<Response<RsVerificationResult>> = await apiJson.get(`${this.baseUrl}/result/${verificationId}`, config)
        if (response.status !== 200) {
            throw new Error(`VerifyAPI getResult: ${response.statusText}`)
        }
        return response.data.data
    }

    async getCurrentStep(verificationId: string): Promise<RsCurrentStep> {
        const config = await getUserInfo(verificationId, null)
        const response = await apiJson.get(`${this.baseUrl}/step/${verificationId}`, config)
        if (response.status !== 200 && response.status !== 412) {
            throw new Error(`VerifyAPI getCurrentStep: ${response.statusText}`)
        }
        return response.data.data
    }

    async getExtractionResult(verificationId: string): Promise<ResultStatusType> {
        const response = await apiJson.get(`${this.baseUrl}/extraction/result/${verificationId}`)
        if (response.status !== 200) {
            throw new Error(`VerifyAPI getExtractionResult: ${response.statusText}`)
        }
        return response.data.data
    }

    async timeoutDocumentFrontStep(verificationId: string) {
        let token = localStorage.getItem("next-step")
        const config = await getUserInfo(verificationId, token)
        const response = await apiJson.put(`${this.baseUrl}/timeout/doc-front/${verificationId}`, null, config)
        if (response.status !== 200) {
            throw new Error(response.statusText)
        }
    }

    async manuallySkipStep(request: RqManuallySkipStep, verificationId: string) {
        let token = localStorage.getItem("next-step")
        const config = await getUserInfo(verificationId, token)
        await apiJson.patch(`${this.baseUrl}/step/${verificationId}/manual-skip`, request, config)
    }

    private async sendMultiPartRequest<T>(formData: FormData, path: string, verificationId: string, headerNeeded: boolean = true): Promise<T> {
        let token = null
        if (headerNeeded) {
            token = localStorage.getItem("next-step")
            localStorage.removeItem("next-step")
        }
        try {
            const config = await getUserInfo(verificationId, token)
            const response: AxiosResponse<Response<T>> = await apiMultipart.post(`${this.baseUrl}/${path}`, formData, config)
            if (response.status !== 200) {
                throw new Error(`VerifyAPI sendMultiPartRequest: ${response.statusText}`)
            }
            return response.data.data
        } catch (error: any) {
            if (error.response !== undefined) {
                if (error.response.status === 412) {
                    throw new MismatchedStepError(convertStringIntoVerificationStepType(error.response.data.correctStep))
                }
                throw new Error(error.response.statusText)
            }
            throw error
        }
    }

    private async sendJsonRequest<T>(path: string, method: string, verificationId: string, body?: T, headerNeeded: boolean = true): Promise<void> {
        try {
            let token = null
            if (headerNeeded) {
                token = localStorage.getItem("next-step")
            }
            const config = await getUserInfo(verificationId, token)
            let response: AxiosResponse<Response<void>>
            switch (method) {
                case 'POST':
                    response = await apiJson.post(`${this.baseUrl}/${path}`, body, config)
                    break
                case 'PUT':
                    response = await apiJson.put(`${this.baseUrl}/${path}`, body, config)
                    break
                case 'PATCH':
                    response = await apiJson.patch(`${this.baseUrl}/${path}`, null, config)
                    break
                default:
                    throw new Error(`${method} not allowed`)
            }
            if (response.status !== 200) {
                throw new Error(response.statusText)
            }
        } catch (error: any) {
            if (error.response !== undefined) {
                if (error.response.status === 412) {
                    throw new MismatchedStepError(convertStringIntoVerificationStepType(error.response.data.correctStep))
                }
                throw new Error(error.response.statusText)
            }
            throw error
        }
    }
}